"use client";

import { c as _c } from "react/compiler-runtime";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { buildPimaEscalationActionText, getPimaAppointmentReason } from "@/src/lib/customer/scheduling/ushv-pima";
import { useReferralSchedulingRecommendation } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { isStatOrUrgentPriority, Referral, SchedulingPriority } from "@/src/models/documents/referral";
import { getFullAddress } from "@/src/models/patient";
import { getProviderNameWithCredentials } from "@/src/models/practitioner";
import { AgenticRecommendation } from "@/src/models/scheduling-recommendation";
function getEscalationReasonText(agenticRecommendation: AgenticRecommendation | null | undefined): string {
  const receivingProviderEvaluation = agenticRecommendation?.evaluatedPractitioners.find(p => p.isReceivingProvider);
  return receivingProviderEvaluation?.reasonCodes?.[0] ?? "N/A";
}
function getPrimaryDiagnosisText(referral: Referral): string | null {
  // PIMA composes reasonForReferral at intake (clinical-gated reason + all ICD descriptions) and ops
  // reviews/approves it, so the Appointment Details box reads that approved field verbatim.
  return referral.reasonForReferral?.trim() || null;
}
function getActionPriorityText(referral: Referral): string {
  const priority = referral.schedulingPriority || SchedulingPriority.ROUTINE;
  return isStatOrUrgentPriority(priority) ? "High" : "Normal";
}
function PimaReferralInstructionsContent(t0) {
  const $ = _c(64);
  const {
    referral,
    schedulingVerificationTaskEnabled
  } = t0;
  let t1;
  if ($[0] !== referral.referringProvider) {
    t1 = getProviderNameWithCredentials(referral.referringProvider);
    $[0] = referral.referringProvider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringProviderName = t1;
  let t2;
  if ($[2] !== referral.receivingProvider) {
    t2 = getProviderNameWithCredentials(referral.receivingProvider);
    $[2] = referral.receivingProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const receivingProviderName = t2;
  let t3;
  if ($[4] !== referral) {
    t3 = getPrimaryDiagnosisText(referral);
    $[4] = referral;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const diagnosis = t3;
  let t4;
  if ($[6] !== referral) {
    t4 = getActionPriorityText(referral);
    $[6] = referral;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const priority = t4;
  let t5;
  if ($[8] !== referral.patient) {
    t5 = getFullAddress(referral.patient);
    $[8] = referral.patient;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const patientAddress = t5;
  const {
    recommendation,
    isPending
  } = useReferralSchedulingRecommendation(referral.id, true);
  let t6;
  if ($[10] !== isPending || $[11] !== recommendation?.chartEligibilityCheck || $[12] !== referral) {
    t6 = isPending ? "Loading\u2026" : getPimaAppointmentReason(referral, recommendation?.chartEligibilityCheck);
    $[10] = isPending;
    $[11] = recommendation?.chartEligibilityCheck;
    $[12] = referral;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const appointmentReason = t6;
  const generalNotes = `Referred by ${referringProviderName} for ${diagnosis || "referral"} - Scheduled via SMS by Valerie Health SMS`;
  let t7;
  if ($[14] !== receivingProviderName || $[15] !== recommendation?.agenticRecommendation || $[16] !== referringProviderName) {
    const escalationReason = getEscalationReasonText(recommendation?.agenticRecommendation);
    t7 = buildPimaEscalationActionText(escalationReason, `Referred to Dr ${receivingProviderName} by Dr ${referringProviderName}.`);
    $[14] = receivingProviderName;
    $[15] = recommendation?.agenticRecommendation;
    $[16] = referringProviderName;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    subject: actionSubject,
    note: actionNotes
  } = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="font-medium text-center">Pima Referral Instructions:</span>;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== referral || $[20] !== schedulingVerificationTaskEnabled) {
    t9 = !schedulingVerificationTaskEnabled && <ReferralSchedulingRecommendation referral={referral} title="Scheduling Recommendation:" />;
    $[19] = referral;
    $[20] = schedulingVerificationTaskEnabled;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="font-semibold text-foreground">Appointment Details:</span>;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="font-medium">Reason:</span>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== appointmentReason) {
    t12 = <div>{t11} <CopyableContent text={appointmentReason} /></div>;
    $[24] = appointmentReason;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="font-medium">Diagnosis:</span>;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = diagnosis || "N/A";
  let t15;
  if ($[27] !== t14) {
    t15 = <div>{t13} <CopyableContent text={t14} /></div>;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="font-medium">Patient Address:</span>;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = patientAddress || "N/A";
  let t18;
  if ($[30] !== t17) {
    t18 = <div>{t16} <CopyableContent text={t17} /></div>;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="font-medium">General Notes:</span>;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== generalNotes) {
    t20 = <div>{t19} <CopyableContent text={generalNotes} /></div>;
    $[33] = generalNotes;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t12 || $[36] !== t15 || $[37] !== t18 || $[38] !== t20) {
    t21 = <div className="flex flex-col gap-2">{t10}<div className="ml-4 space-y-1">{t12}{t15}{t18}{t20}</div></div>;
    $[35] = t12;
    $[36] = t15;
    $[37] = t18;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="font-semibold text-foreground">Action Details:</span>;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="font-medium">Subject:</span>;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== actionSubject) {
    t24 = <div>{t23} <CopyableContent text={actionSubject} /></div>;
    $[42] = actionSubject;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="font-medium">Assigned to:</span>;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  let t27;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div>{t25} <CopyableContent text="CSO,PIMA Referral Management" /></div>;
    t27 = <span className="font-medium">Due date:</span>;
    $[45] = t26;
    $[46] = t27;
  } else {
    t26 = $[45];
    t27 = $[46];
  }
  let t28;
  let t29;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div>{t27} <CopyableContent text="Today" /></div>;
    t29 = <span className="font-medium">Status:</span>;
    $[47] = t28;
    $[48] = t29;
  } else {
    t28 = $[47];
    t29 = $[48];
  }
  let t30;
  let t31;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div>{t29} <CopyableContent text="Not Started" /></div>;
    t31 = <span className="font-medium">Priority:</span>;
    $[49] = t30;
    $[50] = t31;
  } else {
    t30 = $[49];
    t31 = $[50];
  }
  let t32;
  if ($[51] !== priority) {
    t32 = <div>{t31} <CopyableContent text={priority} /></div>;
    $[51] = priority;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  let t33;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <span className="font-medium">Notes:</span>;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== actionNotes) {
    t34 = <div>{t33} <CopyableContent text={actionNotes} /></div>;
    $[54] = actionNotes;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  let t35;
  if ($[56] !== t24 || $[57] !== t32 || $[58] !== t34) {
    t35 = <div className="flex flex-col gap-2">{t22}<div className="ml-4 space-y-1">{t24}{t26}{t28}{t30}{t32}{t34}</div></div>;
    $[56] = t24;
    $[57] = t32;
    $[58] = t34;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== t21 || $[61] !== t35 || $[62] !== t9) {
    t36 = <div className="flex flex-col gap-4">{t8}<div className="text-muted-foreground space-y-4">{t9}{t21}{t35}</div></div>;
    $[60] = t21;
    $[61] = t35;
    $[62] = t9;
    $[63] = t36;
  } else {
    t36 = $[63];
  }
  return t36;
}
function PimaReferralCards(t0) {
  const $ = _c(15);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.patient) {
    t1 = <PatientCard patient={referral.patient} />;
    $[0] = referral.patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== referral) {
    t2 = <ReferralProviderCards referral={referral} />;
    t3 = <ReferralCard referral={referral} isEditable={true} />;
    $[2] = referral;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== referral.primaryCoverage) {
    t4 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[5] = referral.primaryCoverage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== referral.secondaryCoverage) {
    t5 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[7] = referral.secondaryCoverage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
interface PimaReferralInstructionsProps {
  referral: Referral;
  schedulingVerificationTaskEnabled: boolean;
}
export function PimaReferralInstructions(t0) {
  const $ = _c(8);
  const {
    referral,
    schedulingVerificationTaskEnabled
  } = t0;
  let t1;
  if ($[0] !== referral || $[1] !== schedulingVerificationTaskEnabled) {
    t1 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><PimaReferralInstructionsContent referral={referral} schedulingVerificationTaskEnabled={schedulingVerificationTaskEnabled} /></div></EhrInstructionsCard>;
    $[0] = referral;
    $[1] = schedulingVerificationTaskEnabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== referral) {
    t2 = <PimaReferralCards referral={referral} />;
    $[3] = referral;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}